/**
 * Initial (step 1) and final (step 2) submit. Uses fetch or GraphQL (makeContact) when graphqlUrl is set.
 */
import { getOrCreateUrqlClient } from '../graphql/client'
import { MakeContactDocument, toMakeContactVariables } from '../graphql/mutations/makeContact'
import { toInitialContactVariables } from '../graphql/mutations/initialContact'
import type { ContactMeSubmitPayload, ContactMeWidgetOptions } from '../types'
import type { FormState } from './useContactFormState'

function getPropertyIdFromContext(context?: Record<string, unknown>): string | undefined {
  if (!context || typeof context !== 'object') return undefined
  const id = context.propertyId ?? context.property_id
  return id != null ? String(id) : undefined
}

function getDevOverlay() {
  return import.meta.env.DEV ? (window as any).__bostDevOverlay : null
}

export function useContactFormSubmit(
  form: { value: FormState },
  optsRef: { value: ContactMeWidgetOptions },
  openDetailRef: { value: { context?: Record<string, unknown> } | undefined },
  isBusy: { value: boolean },
  setError: (key: keyof FormState, msg: string) => void,
  t: (key: string) => string,
  onStep1Submitted: (payload: {
    email: string
    acceptPrivacyPolicy: boolean
    acceptMarketing: boolean
    source: string
    utm_source: string
    context?: Record<string, unknown>
  }) => void,
  onStep1Error: (err: string) => void,
  onStep2Submitted: (payload: ContactMeSubmitPayload) => void,
  onStep2Error: (err: string) => void
) {
  async function submitInitial(): Promise<void> {
    // Always use GraphQL towards /services/realestategateway/graphql
    isBusy.value = true
    try {
      const payload = {
        email: form.value.email.trim(),
        acceptPrivacyPolicy: form.value.acceptPrivacyPolicy,
        acceptMarketing: form.value.acceptMarketing,
        source: optsRef.value.source ?? 'website',
        utm_source: typeof window !== 'undefined' ? window.location.href : '',
        context: openDetailRef.value?.context,
      }

      const variables = toInitialContactVariables(
        payload.email,
        payload.acceptPrivacyPolicy,
        payload.acceptMarketing,
        payload.source,
        payload.utm_source,
        payload.context
      )

      const devOverlay = getDevOverlay()
      if (devOverlay) {
        // In dev mode, add to dev overlay instead of sending
        devOverlay.addMutation('InitialContact', JSON.stringify(MakeContactDocument), variables)
        onStep1Submitted(payload)
      } else {
        // Actually send the mutation
        const client = getOrCreateUrqlClient()
        const result = await client.mutation(MakeContactDocument, variables).toPromise()
        if (result.error) {
          throw new Error(result.error.message)
        }
        onStep1Submitted(payload)
      }
    } catch (err) {
      onStep1Error(err instanceof Error ? err.message : String(err))
    } finally {
      isBusy.value = false
    }
  }

  async function submitFinal(
    buildPayload: () => ContactMeSubmitPayload,
    onSuccess: (conversationId?: string) => void
  ): Promise<void> {
    const payload = buildPayload()
    const propertyId = getPropertyIdFromContext(openDetailRef.value?.context)

    // Always use GraphQL towards /services/realestategateway/graphql
    isBusy.value = true
    try {
      const variables = toMakeContactVariables(payload, propertyId)

      const devOverlay = getDevOverlay()
      if (devOverlay) {
        // In dev mode, add to dev overlay instead of sending
        const sendFn = async () => {
          const client = getOrCreateUrqlClient()
          const result = await client.mutation(MakeContactDocument, variables).toPromise()
          if (result.error) {
            throw new Error(result.error.message)
          }
          return result
        }
        devOverlay.addMutation(
          'MakeContact',
          JSON.stringify(MakeContactDocument),
          variables,
          sendFn
        )
        onStep2Submitted(payload)
        onSuccess('dev-mock-id')
      } else {
        // Actually send the mutation
        const client = getOrCreateUrqlClient()
        const result = await client.mutation(MakeContactDocument, variables).toPromise()
        if (result.error) {
          throw new Error(result.error.message)
        }
        const id = result.data?.makeContact?.id
        onStep2Submitted(payload)
        onSuccess(id != null ? String(id) : undefined)
      }
    } catch (err) {
      onStep2Error(err instanceof Error ? err.message : 'Error')
      setError('email', t('errors.send_failed'))
    } finally {
      isBusy.value = false
    }
  }

  return { submitInitial, submitFinal }
}
